import { useState, useEffect, useMemo, useCallback } from "react"
import { TrustCenterProvider } from "./TrustCenterContext"
import { buildTrustCenterData, isWidgetVisible } from "./data/buildTrustCenterData"
import IconSprite from "./ui/IconSprite"
import { Header, Hero, Footer, useAdminPath, BASE_NAV } from "./shell/Shell"
import {
    CertificationsSection,
    ControlsSection,
    DocumentsSection,
    SubprocessorsSection,
    FaqSection,
} from "./views/SinglePageSections"
import "../styles/darwinsans.css"
import "../styles/trust-center/tokens.css"
import "../styles/trust-center/styles.css"

const HEADER_OFFSET = 80

function buildNav(widgets) {
    return BASE_NAV
        .filter((item) => !item.widget || isWidgetVisible(widgets, item.widget))
        .map(({ id, label }) => ({ id, label }))
}

function scrollToSection(id) {
    if (id === "overview") {
        window.scrollTo({ top: 0, behavior: "smooth" })
        return
    }
    const el = document.getElementById(id)
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET
    window.scrollTo({ top, behavior: "smooth" })
}

export default function TrustCenterApp({
    documents,
    widgets,
    controls,
    user,
    myRequests,
    onLogin,
    onLogout,
    onRequestAccess,
    onDownload,
    onView,
    onLoginRequired,
    downloadingDocId = null,
}) {
    const tcData = useMemo(
        () => buildTrustCenterData({ documents, widgets, controls }),
        [documents, widgets, controls]
    )

    const nav = useMemo(() => buildNav(widgets), [widgets])
    const sectionIds = useMemo(() => nav.map((n) => n.id), [nav])

    const showSubprocessors = isWidgetVisible(widgets, "Subprocessors")
    const showFaq = isWidgetVisible(widgets, "FAQ")

    const [activeSection, setActiveSection] = useState("overview")
    const goAdmin = useAdminPath(user)

    const go = useCallback((target) => {
        scrollToSection(target)
        window.history.replaceState(null, "", target === "overview" ? "#overview" : `#${target}`)
    }, [])

    useEffect(() => {
        const ids = sectionIds.filter((id) => id !== "overview")
        const onScroll = () => {
            const probe = window.scrollY + HEADER_OFFSET + 12
            let current = "overview"
            for (const id of ids) {
                const el = document.getElementById(id)
                if (el && el.offsetTop <= probe) current = id
            }
            if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 4) {
                current = ids[ids.length - 1] || "overview"
            }
            setActiveSection(current)
        }
        onScroll()
        window.addEventListener("scroll", onScroll, { passive: true })
        window.addEventListener("resize", onScroll)
        return () => {
            window.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", onScroll)
        }
    }, [sectionIds])

    useEffect(() => {
        const id = (window.location.hash || "").replace("#", "")
        if (id && sectionIds.includes(id)) {
            setTimeout(() => scrollToSection(id), 120)
        }
    }, [sectionIds])

    return (
        <TrustCenterProvider value={tcData}>
            <div className="trust-center-app">
                <IconSprite />
                <Header
                    view={activeSection}
                    go={go}
                    nav={nav}
                    user={user}
                    onLogin={onLogin}
                    onLogout={onLogout}
                    onAdmin={user?.role === "admin" ? goAdmin : null}
                />
                <main>
                    <Hero />
                    <CertificationsSection />
                    <ControlsSection />
                    <DocumentsSection
                        user={user}
                        myRequests={myRequests}
                        onRequestAccess={onRequestAccess}
                        onDownload={onDownload}
                        onView={onView}
                        onLoginRequired={onLoginRequired}
                        downloadingDocId={downloadingDocId}
                    />
                    {showSubprocessors && <SubprocessorsSection />}
                    {showFaq && <FaqSection />}
                </main>
                <Footer />
            </div>
        </TrustCenterProvider>
    )
}
